import { compile } from '../chart/compile.ts';
import { bezZnacznikowUg } from '../chart/preNormalize.ts';
import type { Utwor, UtworId } from '../store/db.ts';

// Ekran wklejenia i edycji utworu. Jeden formularz obsługuje oba przypadki
// (nowy utwór i edycja istniejącego), bo różnią się tylko tym, co robić po
// zapisie i czy jest przycisk usunięcia, a nie kształtem pól.
//
// TO JEST JEDYNY EKRAN W APLIKACJI, NA KTÓRYM WOLNO PRZEWIJAĆ: obietnica
// "wszystko na jednym ekranie" z `render/` dotyczy GRANIA, nie wklejania
// długiego taba z klawiatury telefonu.

export interface WklejanieDeps {
  /** `null` = nowy utwór, w przeciwnym razie edycja istniejącego. */
  utwor: Utwor | null;
  naZapisz: (utwor: Utwor) => void | Promise<void>;
  naUsun: (id: UtworId) => void | Promise<void>;
  naPowrot: () => void;
}

function nowyId(): UtworId {
  // Bez `crypto.randomUUID()`: opieranie jednego identyfikatora na wsparciu
  // przeglądarki dla całego Web Crypto nie jest tego warte, wystarczy coś
  // unikalnego w obrębie jednej instalacji aplikacji.
  return `reczny:${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

const CZAS_ODBICIA_MS = 300;

export function mountujWklejanie(kontener: HTMLElement, deps: WklejanieDeps): void {
  const edycja = deps.utwor !== null;

  kontener.replaceChildren();
  kontener.className = 'zar-wklejanie';

  const naglowek = document.createElement('header');
  naglowek.className = 'zar-naglowek zar-naglowek--male';
  naglowek.innerHTML = `<h1>${edycja ? 'edycja utworu' : 'nowy utwór'}</h1>`;
  kontener.appendChild(naglowek);

  const formularz = document.createElement('form');
  formularz.className = 'zar-form';
  formularz.innerHTML = `
    <label class="zar-pole">
      <span>Tytuł</span>
      <input name="tytul" type="text" required autocomplete="off" />
    </label>
    <label class="zar-pole">
      <span>Wykonawca</span>
      <input name="wykonawca" type="text" autocomplete="off" />
    </label>
    <label class="zar-pole zar-pole--tab">
      <span>Tab z akordami</span>
      <textarea name="rawTab" rows="14" spellcheck="false"></textarea>
    </label>
    <div class="zar-diagnostyka"></div>
    <div class="zar-akcje">
      <button type="button" class="zar-przycisk zar-przycisk--wtorny" data-akcja="powrot">wróć</button>
      ${edycja ? '<button type="button" class="zar-przycisk zar-przycisk--niebezpieczny" data-akcja="usun">usuń</button>' : ''}
      <button type="submit" class="zar-przycisk zar-przycisk--glowny" data-akcja="zapisz">zapisz</button>
    </div>
  `;
  kontener.appendChild(formularz);

  const poleTytul = formularz.querySelector<HTMLInputElement>('input[name="tytul"]')!;
  const poleWykonawca = formularz.querySelector<HTMLInputElement>('input[name="wykonawca"]')!;
  const poleTab = formularz.querySelector<HTMLTextAreaElement>('textarea[name="rawTab"]')!;
  const diagCel = formularz.querySelector<HTMLDivElement>('.zar-diagnostyka')!;
  const przyciskZapisz = formularz.querySelector<HTMLButtonElement>('[data-akcja="zapisz"]')!;

  if (deps.utwor) {
    poleTytul.value = deps.utwor.tytul;
    poleWykonawca.value = deps.utwor.wykonawca ?? '';
    poleTab.value = bezZnacznikowUg(deps.utwor.rawTab);
  }
  // Tab z UG niesie znaczniki `[tab]` i `[ch]`, które w edytorze tylko
  // zasłaniały tekst. Pokazujemy go bez nich, a jeśli nikt niczego nie
  // zmienił, zapis zostawia oryginał nietknięty.
  const tabPokazany = poleTab.value;
  const tabDoZapisu = (): string =>
    deps.utwor && poleTab.value === tabPokazany ? deps.utwor.rawTab : poleTab.value;

  // Dopóki diagnostyka nie policzyła się choć raz, zapis ma być zablokowany:
  // utwór z błędem kompilacji i tak nie da się zagrać, więc lepiej powiedzieć
  // to PRZED zapisem niż dopiero po zamknięciu ekranu.
  let zapisZablokowany = true;

  function przeliczDiagnostyke(): void {
    const tekst = poleTab.value;
    if (tekst.trim() === '') {
      diagCel.innerHTML = '<p class="zar-diag-linia">Wklej tab, a tu pojawi się, ile sekcji rozpoznałem i jaki jest układ utworu.</p>';
      zapisZablokowany = true;
      przyciskZapisz.disabled = true;
      return;
    }

    const chart = compile(deps.utwor?.id ?? 'podglad', tekst);
    zapisZablokowany = chart.diagnostics.some((d) => d.severity === 'error');
    przyciskZapisz.disabled = zapisZablokowany;

    diagCel.replaceChildren();
    const podsumowanie = document.createElement('p');
    podsumowanie.className = 'zar-diag-linia';
    podsumowanie.textContent = `Sekcje: ${chart.sections.length} · układ: ${chart.layoutMap || '(brak)'}`;
    diagCel.appendChild(podsumowanie);

    for (const d of chart.diagnostics) {
      const wiersz = document.createElement('p');
      wiersz.className = d.severity === 'error' ? 'zar-diag-linia zar-diag-linia--blad' : 'zar-diag-linia';
      wiersz.textContent = d.message;
      diagCel.appendChild(wiersz);
    }
  }

  let watekOdbicia: ReturnType<typeof setTimeout> | null = null;
  poleTab.addEventListener('input', () => {
    if (watekOdbicia !== null) clearTimeout(watekOdbicia);
    watekOdbicia = setTimeout(przeliczDiagnostyke, CZAS_ODBICIA_MS);
  });

  // Pierwsze przeliczenie od razu, bez czekania na odbicie: edycja
  // istniejącego utworu ma pokazać prawdziwy stan, nie fałszywe "wklej tab".
  przeliczDiagnostyke();

  formularz.addEventListener('submit', (zdarzenie) => {
    zdarzenie.preventDefault();
    if (zapisZablokowany) return; // bramka klawiszem Enter, przycisk jest już disabled

    const teraz = new Date().toISOString();
    const zapis: Utwor = deps.utwor
      ? {
          ...deps.utwor,
          tytul: poleTytul.value.trim(),
          wykonawca: poleWykonawca.value.trim() || null,
          rawTab: tabDoZapisu(),
          zmienionyO: teraz,
        }
      : {
          id: nowyId(),
          tytul: poleTytul.value.trim(),
          wykonawca: poleWykonawca.value.trim() || null,
          zrodlo: 'reczny',
          rawTab: tabDoZapisu(),
          dodanyO: teraz,
          zmienionyO: null,
        };
    void deps.naZapisz(zapis);
  });

  formularz.querySelector('[data-akcja="powrot"]')?.addEventListener('click', () => deps.naPowrot());

  formularz.querySelector('[data-akcja="usun"]')?.addEventListener('click', () => {
    if (!deps.utwor) return;
    // Jedno potwierdzenie zawsze: skasowanie cudzej pracy bez pytania jest w
    // tym repozytorium osobno zapisanym błędem, więc nie ma tu ścieżki na skróty.
    if (!confirm(`Usunąć "${deps.utwor.tytul}"? Tej operacji nie da się cofnąć.`)) return;
    void deps.naUsun(deps.utwor.id);
  });
}
